import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import type ReCAPTCHA from 'react-google-recaptcha';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import type { AccountApiError } from '@/types/accountApiType';

import { CheckboxField } from '@/components/CheckboxField';
import { Col } from '@/components/Col';
import { Flex } from '@/components/Flex';
import { TitleField } from '@/components/FormFields/TitleField';
import { FormProvider } from '@/components/FormProvider';
import { InputField } from '@/components/InputField';
import { LegacyButton } from '@/components/LegacyButton';
import { ReCaptcha } from '@/components/ReCaptcha';
import { SelectField } from '@/components/SelectField';
import { Typography } from '@/components/Typography';
import { UploadInputField } from '@/components/UploadInputField/UploadInputField';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import { CONTENTSQUARE_MASKED } from '@/lib/forms/const';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import { getMetaValue } from '@/lib/metadata/helpers';
import { paths } from '@/lib/paths';
import { parsePhoneNumber } from '@/lib/phone';
import { getPhonePrefixSelectOpts } from '@/lib/phonePrefixes/helpers';
import { archivesSendEmail } from '@/lib/service/api';
import type { ArchivesAttachment } from '@/lib/service/type';
import { ArchivesPrivacyPolicy } from '@/messages/archives';
import { commonMessages } from '@/messages/common';
import { formMessages } from '@/messages/forms';
import * as M from '@/messages/newsletter';
import { serviceMessages } from '@/messages/service';
import { useRegionProvider } from '@/providers/RegionProvider';
import { archivesSchema } from '@/views/ArchivesView/schema';
import * as S from '@/views/NewsLetterView/NewsLetterView.styles';

import type { ArchivesSchema } from './schema';

import * as ArchivesViewStyle from './ArchivesView.styles';

export const ArchivesForm = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { push } = useRouter();
  const {
    snapshot: { user },
  } = useUser();
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const [isCaptchaStatus, setIsCaptchaStatus] = useState(false);
  const { formatMessage } = useIntl();
  const {
    country,
    query: { languageCode },
  } = useRegionProvider();

  const schema = archivesSchema(formatMessage, country, user);
  const defaultValues = {
    ...schema.getDefault(),
  };
  const formCtx = useForm({
    defaultValues,
    resolver: yupResolver(schema),
  });
  const captcha = formCtx.watch('captcha');
  useEffect(() => {
    if (user?.id) {
      formCtx.reset(defaultValues);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.metadata) {
      const _phone = getMetaValue(user.metadata, ACCOUNT_METADATA.phone) ?? '';
      formCtx.setValue('phone', _phone);
    }
  }, [user?.metadata]);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<AccountApiError | undefined>(undefined);

  const handleSubmit: SubmitHandler<ArchivesSchema> = async (
    formData: ArchivesSchema,
  ) => {
    try {
      setLoading(true);
      const fullPhoneNumber = formData.phonePrefix.value + formData.phone;
      const phoneNumber = parsePhoneNumber(fullPhoneNumber);

      const now = new Date();

      const millisecondsTimestamp = now.getTime();

      const secondsTimestamp = Math.floor(
        millisecondsTimestamp / 1000,
      ).toString();

      const attachments = formData.attachments.map((item) => ({
        ...item,
        base64: item?.base64?.replace(/^data:image\/\w+;base64,/, ''),
      }));
      const submitObj = {
        ...formData,
        title: formData.title?.value ?? '',
        phoneNumber: phoneNumber?.number ?? '',
        country: country.code,
        channel_slug: country.channelSlug,
        attachments: attachments as ArchivesAttachment[],
        id: secondsTimestamp,
      };

      const res = await archivesSendEmail(submitObj);
      if (res?.success) {
        enqueueSnackbar(
          formatMessage(commonMessages.commonSubmittedSuccessfully),
          {
            variant: 'success',
          },
        );
        push(paths.archives.confirm.asPath());
      } else {
        setError({ code: 500, msg: res?.message });
      }
    } catch (error: any) {
      setError({ code: error?.code, msg: error?.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormProvider
      name='archivesForm'
      {...formCtx}
      onSubmit={(data) => {
        handleSubmit({
          ...data,
        });
      }}
    >
      <S.ArchivesFormGroup>
        <S.ArchivesFormRow gapDensity='none'>
          <Col>
            <InputField
              key='modelReference'
              autoComplete='given-name'
              colorVariant='grey'
              data-testid='ArchivesFormModelReference'
              errorVariant='full'
              name='modelReference'
              placeholder={formatMessage(commonMessages.reference)}
            />
          </Col>
          <Col>
            <InputField
              autoComplete='given-name'
              colorVariant='grey'
              data-testid='ArchivesFormSerialNumber'
              errorVariant='full'
              name='serialNumber'
              placeholder={formatMessage(commonMessages.serialNumber)}
            />
          </Col>
          <Col data-testid='ArchivesFormPhonePrefixSelectField'>
            <TitleField colorVariant='grey' countryCode={country.code} />
          </Col>
          <Col>
            <InputField
              autoComplete='given-name'
              className={CONTENTSQUARE_MASKED}
              colorVariant='grey'
              data-testid='ArchivesFormFirstName'
              errorVariant='full'
              name='firstName'
              placeholder={formatMessage(formMessages.firstName)}
              required
            />
          </Col>
          <Col>
            <InputField
              autoComplete='additional-name'
              className={CONTENTSQUARE_MASKED}
              colorVariant='grey'
              data-testid='ArchivesFormLastName'
              errorVariant='full'
              name='lastName'
              placeholder={formatMessage(formMessages.lastName)}
              required
            />
          </Col>
          <Col>
            <InputField
              autoComplete='email'
              colorVariant='grey'
              data-testid='ArchivesFormEmail'
              errorVariant='full'
              name='email'
              placeholder={formatMessage(formMessages.email)}
              required
              type='email'
            />
          </Col>
          <Col>
            <SelectField
              colorVariant='grey'
              data-testid='ArchivesFormPhonePrefixSelectField'
              name='phonePrefix'
              options={getPhonePrefixSelectOpts()}
            />
          </Col>
          <Col>
            <Flex flexDirection='column'>
              <InputField
                autoComplete='phone'
                className={CONTENTSQUARE_MASKED}
                colorVariant='grey'
                data-testid='ArchivesFormPhoneInputField'
                errorVariant='full'
                id='ArchivesFormPhoneInputField'
                name='phone'
                pattern='[0-9]+'
                placeholder={formatMessage(formMessages.telephoneNumber)}
                required
                type='text'
              />
            </Flex>
          </Col>
          <Col data-testid='upload-input-field'>
            <UploadInputField name='attachments'></UploadInputField>
          </Col>
          <Col>
            <ArchivesViewStyle.CommentTextArea
              name='comment'
              placeholder={formatMessage(serviceMessages.comment)}
              rows={6}
            />
          </Col>
        </S.ArchivesFormRow>
        <S.Row gapDensity='none'>
          <Col md={12}>
            <Typography color='secondaryGrey' scale='p3' textAlign='right'>
              {formatMessage(formMessages.mandatoryFields)}
            </Typography>
          </Col>
          <Col data-testid='ArchivesFormAccountRegisterCheckbox'>
            <CheckboxField
              data-testid='checkboxArchivesConsent'
              name='archivesConsent'
            >
              <Flex flexDirection='column' gridGap='s075'>
                <ArchivesViewStyle.Text data-testid='ArchivesPrivacyPolicy'>
                  <ArchivesPrivacyPolicy></ArchivesPrivacyPolicy>
                </ArchivesViewStyle.Text>
              </Flex>
            </CheckboxField>
          </Col>
          <Col>
            <ReCaptcha
              ref={reCaptchaRef}
              data-testid='checkboxCaptcha'
              languageCode={languageCode}
              name='captcha'
              onChange={setIsCaptchaStatus}
            />
          </Col>
          <Col>
            <Flex justifyContent='center'>
              <LegacyButton
                data-testid='ArchivesFormButtonSubmit'
                disabled={loading || !captcha || isCaptchaStatus}
                isProcessing={loading}
                label={formatMessage(M.defined.newsLetterButton)}
                style={{ height: 48 }}
                type='submit'
              />
            </Flex>
          </Col>
        </S.Row>
        {!isEmpty(error) && error.msg && (
          <Typography
            color='error'
            data-testid='ArchivesErrorMessage'
            scale='p3'
            textAlign='center'
          >
            {error.msg}
          </Typography>
        )}
      </S.ArchivesFormGroup>
    </FormProvider>
  );
};
